import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PaymentHistory } from '../components/PaymentHistory';
import { InvoiceHistory } from '../components/InvoiceHistory';
import { userService } from '../lib/userService';
import { supabase } from '../lib/supabase';

interface UserProfile {
  full_name?: string;
  avatar_url?: string;
}

export const Settings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile>({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [showInvoiceHistory, setShowInvoiceHistory] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    email: ''
  });

  useEffect(() => {
    if (user) {
      setProfile({
        full_name: user.user_metadata?.full_name || '',
        avatar_url: user.user_metadata?.avatar_url || ''
      });
      setFormData({
        full_name: user.user_metadata?.full_name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setLoading(true);
    setMessage(null);

    try {
      // Update user metadata in Supabase Auth
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          full_name: formData.full_name
        }
      });

      if (authError) throw authError;

      // Update user record in database
      const { error: dbError } = await supabase
        .from('users')
        .update({
          full_name: formData.full_name,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (dbError) throw dbError;

      // Update local user state
      const updatedUser = {
        ...user,
        user_metadata: {
          ...user.user_metadata,
          full_name: formData.full_name
        }
      };
      updateUser(updatedUser);

      setProfile({
        full_name: formData.full_name,
        avatar_url: profile.avatar_url
      });

      setIsEditing(false);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      full_name: profile.full_name || '',
      email: user?.email || ''
    });
    setIsEditing(false);
    setMessage(null);
  };

  const refreshTokenBalance = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      // Clear cache to force fresh data
      userService.clearUserCache(user.id);
      // This will trigger a re-fetch in the AuthContext
      window.location.reload();
    } catch (error) {
      console.error('Error refreshing token balance:', error);
      setMessage({ type: 'error', text: 'Failed to refresh token balance.' });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-gray-600">Manage your account settings and preferences</p>
        </div>

        {/* Token Balance Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Token Balance</h2>
              <p className="text-gray-600">Your current token balance for AI generations</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600 mb-1">
                🪙 {user.tokens || 0}
              </div>
              <button
                onClick={refreshTokenBalance}
                disabled={loading}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
              >
                Edit
              </button>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  placeholder="Email address"
                />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>

              {message && (
                <div className={`p-3 rounded-md text-sm ${
                  message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  {message.text}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handleSaveProfile}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Full Name</span>
                <p className="text-gray-900">{profile.full_name || 'Not set'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Email Address</span>
                <p className="text-gray-900">{user.email}</p>
              </div>
            </div>
          )}
        </div>

        {/* Billing Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Billing & Payments</h2>
          <p className="text-gray-600 mb-6">View your payment history and manage billing information</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setShowPaymentHistory(true)}
              className="flex items-center justify-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="text-center">
                <div className="text-2xl mb-2">💳</div>
                <h3 className="font-medium text-gray-900">Payment History</h3>
                <p className="text-sm text-gray-600">View your past payments</p>
              </div>
            </button>
            
            <button
              onClick={() => setShowInvoiceHistory(true)}
              className="flex items-center justify-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="text-center">
                <div className="text-2xl mb-2">📄</div>
                <h3 className="font-medium text-gray-900">Invoice History</h3>
                <p className="text-sm text-gray-600">Download and view invoices</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Payment History Modal */}
      {showPaymentHistory && (
        <PaymentHistory onClose={() => setShowPaymentHistory(false)} />
      )}

      {/* Invoice History Modal */}
      {showInvoiceHistory && (
        <InvoiceHistory onClose={() => setShowInvoiceHistory(false)} />
      )}
    </div>
  );
}; 